import { ActionConfirmation, usePublishActionOutcome, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError } from "../api.js";
import { DateField } from "../calendar/DateField.js";
import { dayKey, formatDate, formatDay, formatFullDay, formatTime, isoDay } from "../format.js";
import {
  idempotencyKey,
  type BookingOffering,
  type LessonCreditBlock,
} from "./api.js";
import {
  availabilityWindow,
  groupAvailabilitySlots,
  moveAvailabilityWindow,
  type AvailabilityRange,
} from "./availabilityRange.js";
import {
  bookingAttemptAfterFailure,
  bookingFailureKind,
  uncertainBookingMessageFor,
} from "./attempt.js";
import {
  CreateRecurringLesson,
  type RecurringLessonContext,
} from "./CreateRecurringLesson.js";
import { defaultBookingOffering } from "./offeringDefault.js";
import {
  staffBooking,
  type StaffAvailability,
  type StaffLessonBookingResult,
  type TutorCancellationResult,
} from "./staff.js";
import { staffWorkingHours, type StaffSlotCheck } from "./staffWorkingHours.js";
import {
  defaultFundingMode,
  fundingModes,
  offeringKey,
  type TutorFundingMode,
} from "./tutorBookingFunding.js";
import { tutorOneOffBooking } from "./tutorOneOffBooking.js";
import { BookingConfirmationEmailControl } from "./BookingConfirmationEmailControl.js";
import { measureAction } from "../telemetry/actionTiming.js";

type TutorBookingMode = "one" | "repeats";

export function tutorBookingFailure(cause: unknown): string {
  if (bookingFailureKind(cause) === "uncertain") return uncertainBookingMessageFor(cause);
  if (cause instanceof ApiError) {
    if (cause.code === "staff_booking_prewrite_rejected") {
      return "No appointment request was sent to Amelia. Refresh the selected time and try again.";
    }
    if (cause.code === "credit_not_available") {
      // Not every offering supports payment-pending, so this cannot promise
      // that option is on screen — only that credit funding is not.
      return "There is no available lesson credit for this booking. Choose a different funding option if one is offered, or refresh the lesson options.";
    }
    if (cause.code === "slot_unavailable") {
      return "That slot is no longer available. Please choose another time.";
    }
    if (cause.status === 403 || cause.code === "forbidden") {
      return "You are not allowed to book this student.";
    }
    if (cause.status === 409 || cause.code === "conflict") {
      return "That slot or funding state is no longer available. Please refresh and choose again.";
    }
  }
  return "We could not create this booking.";
}

export function tutorBookingAttemptAfterFailure(
  currentKey: string,
  cause: unknown,
  createKey: () => string = idempotencyKey,
) {
  const attempt = bookingAttemptAfterFailure(currentKey, cause, createKey);
  return {
    ...attempt,
    message: tutorBookingFailure(cause),
  };
}

export function tutorAvailabilityFailure(cause: unknown): string {
  if (cause instanceof ApiError && (cause.code === "upstream_unavailable" || cause.status >= 500)) {
    return "Availability is temporarily unavailable. Please try again shortly.";
  }
  return "We could not load available times. Please try again.";
}

export function tutorOfferingFailure(cause: unknown): string {
  if (cause instanceof ApiError && (cause.code === "upstream_unavailable" || cause.status >= 500)) {
    return "Lesson options are temporarily unavailable. Please try again shortly.";
  }
  if (cause instanceof ApiError && (cause.status === 403 || cause.code === "forbidden")) {
    return "You are not allowed to book this student.";
  }
  return "We could not load the lesson options. Please try again.";
}

export function tutorBookingSuccess(result: StaffLessonBookingResult): string {
  const prefix = result.replayed ? "Booking confirmed again" : "Booking created";
  const sync = result.mirror_sync.state === "refreshed"
    ? ""
    : " The diary is still updating. This lesson is booked; do not book it again.";
  const when = `for ${formatDate(result.starts_at)} at ${formatTime(result.starts_at)}.${sync}`;

  if (result.credit.effect === "consume" && result.credit.credits === 1) {
    return `${prefix} ${when} 1 lesson credit was used.`;
  }
  if (result.credit.effect === "consume" && result.credit.credits > 1) {
    return `${prefix} ${when} ${result.credit.credits} lesson credits were used.`;
  }
  if (result.payment.state === "pending") {
    return `${prefix} ${when} Payment remains due and can be marked as received later.`;
  }
  if (result.payment.state === "not_required") {
    return `${prefix} ${when} No payment is due for this booking.`;
  }
  return `${prefix} ${when} The booking system returned payment state “${result.payment.state}” and credit effect “${result.credit.effect}”.`;
}

export function tutorBookingOutcome(
  result: StaffLessonBookingResult,
  studentName?: string | null,
  serviceName?: string | null,
  undoAction?: ReactNode,
): ActionOutcome {
  const hasAppointmentId = typeof result.appointment_id === "number" &&
    Number.isSafeInteger(result.appointment_id) && result.appointment_id > 0;
  return {
    variant: result.mirror_sync.state === "refreshed" ? "success" : "attention",
    title: "Lesson booked",
    description: `${studentName ? `${studentName} · ` : ""}${formatDate(result.starts_at)} · ${formatTime(result.starts_at)}`,
    details: [
      ...(serviceName ? [{ value: serviceName }] : []),
      { value: result.credit.effect === "consume" ? `${result.credit.credits} lesson credit${result.credit.credits === 1 ? "" : "s"} used` : "No lesson credits used" },
      { label: "Payment state", value: result.payment.state },
      ...(result.mirror_sync.state !== "refreshed" ? [{ value: "The lesson is booked, but the diary is still updating. Do not book it again." }] : []),
      ...(result.replayed ? [{ value: "This confirms the earlier booking. No additional lesson was booked." }] : []),
    ],
    ...(hasAppointmentId && undoAction != null ? { action: undoAction } : {}),
  };
}

const BOOKING_UNDO_WINDOW_MS = 10 * 60 * 1000;

function UndoBookingButton({
  appointmentId,
  bookedAt,
  notifyParticipants,
  studentName,
  startsAt,
  onBooked,
  onLocalOutcome,
}: {
  appointmentId: number;
  bookedAt: number;
  notifyParticipants: boolean;
  studentName?: string | null;
  startsAt: string;
  onBooked: () => void;
  onLocalOutcome: (outcome: ActionOutcome) => void;
}) {
  const publish = usePublishActionOutcome();
  const attempted = useRef(false);
  const [disabled, setDisabled] = useState(false);

  function finish(outcome: ActionOutcome) {
    if (!publish(outcome)) onLocalOutcome(outcome);
  }

  async function undo() {
    if (attempted.current) return;
    attempted.current = true;
    setDisabled(true);

    if (Date.now() - bookedAt > BOOKING_UNDO_WINDOW_MS) {
      finish({
        variant: "attention",
        title: "Too late to undo here. Cancel the lesson from the diary instead.",
      });
      return;
    }

    let result: TutorCancellationResult;
    try {
      result = await staffBooking.cancelAppointment(
        String(appointmentId),
        "restore",
        idempotencyKey(),
        notifyParticipants,
        null,
      );
    } catch (cause) {
      const uncertain = bookingFailureKind(cause) === "uncertain";
      const message = cause instanceof Error && cause.message.trim() !== ""
        ? cause.message
        : "The booking could not be undone.";
      finish({
        variant: uncertain ? "uncertain" : "attention",
        title: uncertain ? "Undo outcome needs checking" : "Could not undo booking",
        description: `${studentName ? `${studentName} · ` : ""}${formatDate(startsAt)} · ${formatTime(startsAt)}`,
        details: [{ value: message }],
      });
      return;
    }

    const creditsRestored = result.credits_restored;
    finish({
      variant: "success",
      title: "Booking undone",
      description: `${studentName ? `${studentName} · ` : ""}${formatDate(startsAt)} · ${formatTime(startsAt)}`,
      details: [{ value: `${creditsRestored} lesson credit${creditsRestored === 1 ? "" : "s"} restored.` }],
    });
    onBooked();
  }

  return <button type="button" disabled={disabled} onClick={() => void undo()}>Undo booking</button>;
}

export function tutorBookingWorkingHoursOverride(
  check: StaffSlotCheck | null,
  selectedStart: string | null,
): boolean {
  const overrideAvailable =
    check?.schedule_override_available ?? check?.working_hours_override_available;
  return (
    selectedStart !== null &&
    check?.starts_at === selectedStart &&
    (check.state === "historical" ||
      check.state === "within_notice_period" ||
      check.state === "blocked_time" ||
      check.state === "outside_working_hours") &&
    overrideAvailable === true
  );
}

export function tutorBookingScheduleOverrideMessage(check: StaffSlotCheck | null): string {
  switch (check?.state) {
    case "historical":
      return "This lesson time is in the past. Staff can record it as a historical lesson.";
    case "within_notice_period":
      return "This lesson is inside the normal minimum notice period. Staff can book it anyway.";
    case "blocked_time":
      return "This time is blocked in the tutor's diary. Staff can book this lesson over the block; the block will remain in place.";
    case "outside_working_hours":
      return "This lesson is outside the tutor's normal teaching hours. Check the details, then choose Book anyway to continue.";
    default:
      return "This time needs a staff schedule override.";
  }
}

interface TutorBookLessonViewProps {
  appointmentMode?: boolean;
  showHeading?: boolean;
  offerings: BookingOffering[] | null;
  selectedOfferingKey: string | null;
  onSelectOffering: (key: string) => void;
  fundingMode: TutorFundingMode | null;
  onFundingMode: (mode: TutorFundingMode) => void;
  studentName?: string | null;
  serviceName: (offering: BookingOffering) => string;
  preferredDate: string;
  onPreferredDate: (day: string) => void;
  availabilityRange?: AvailabilityRange;
  onAvailabilityRange?: (range: AvailabilityRange) => void;
  onMoveAvailabilityRange?: (direction: -1 | 1) => void;
  availability: StaffAvailability["slots"] | null;
  selectedStart: string | null;
  workingHoursCheck: StaffSlotCheck | null;
  onSelectStart: (startsAt: string) => void;
  onFindTimes: () => void;
  onConfirm: () => void;
  notifyParticipants?: boolean;
  onNotifyParticipants?: (notify: boolean) => void;
  notifyParticipantsDisabled?: boolean;
  busy: "offerings" | "availability" | "booking" | null;
  error: string | null;
  done: string | null;
  outcome?: ActionOutcome | null;
  uncertain?: boolean;
}

/**
 * Presentation for the one-slot tutor booking action. Every provider/service,
 * funding and availability decision displayed here came from WMF-SIte; this
 * view does not calculate ownership, credit balances or booking policy.
 */
export function TutorBookLessonView({
  appointmentMode = false,
  showHeading = true,
  offerings,
  selectedOfferingKey,
  onSelectOffering,
  fundingMode,
  onFundingMode,
  studentName = null,
  serviceName,
  preferredDate,
  onPreferredDate,
  availabilityRange = "week",
  onAvailabilityRange = () => undefined,
  onMoveAvailabilityRange = () => undefined,
  availability,
  selectedStart,
  workingHoursCheck,
  onSelectStart,
  onFindTimes,
  onConfirm,
  notifyParticipants = false,
  onNotifyParticipants = () => undefined,
  notifyParticipantsDisabled = false,
  busy,
  error,
  done,
  outcome = null,
  uncertain = false,
}: TutorBookLessonViewProps) {
  const selectableOfferings = (offerings ?? []).filter((offering) => fundingModes(offering).length > 0);
  const selectedOffering =
    selectableOfferings.find((offering) => offeringKey(offering) === selectedOfferingKey) ?? null;
  const modes = selectedOffering === null ? [] : fundingModes(selectedOffering);
  const workingHoursOverride = tutorBookingWorkingHoursOverride(workingHoursCheck, selectedStart);
  const window = availabilityWindow(preferredDate || isoDay(0), availabilityRange);
  const today = isoDay(0);
  // The add action looks times up by itself after each change and discards a
  // stale reply, so its choices stay editable during that read. Disabling a
  // focused date box mid-entry would drop the rest of what is being typed.
  const choicesLocked = appointmentMode ? busy === "booking" : busy !== null;

  if (done) {
    return (
      <div className="panel">
        <ActionConfirmation {...(outcome ?? { variant: "success", title: "Lesson booked", description: done })} reveal />
      </div>
    );
  }

  if (offerings === null) {
    return (
      <div className="panel">
        {showHeading && <h3 style={{ marginTop: 0 }}>Book a lesson</h3>}
        <p className="empty">{busy === "offerings" ? "Loading lesson options…" : "Lesson options are unavailable."}</p>
        {error && <div className="error">{error}</div>}
      </div>
    );
  }

  if (selectableOfferings.length === 0) {
    return (
      <div className="panel">
        {showHeading && <h3 style={{ marginTop: 0 }}>Book a lesson</h3>}
        <p className="empty">No lesson service is available in this student and tutor scope.</p>
        {error && <div className="error">{error}</div>}
      </div>
    );
  }

  return (
    <div className="panel tutor-booking-form">
      {showHeading && <h3 style={{ marginTop: 0 }}>Book a lesson</h3>}
      <p className="small muted">
        {appointmentMode
          ? "Choose a lesson type, then a date to see available times."
          : "Choose the lesson type and how it will be paid for. Check the selected time below, or choose another available time."}
      </p>

      <label className="stack">
        <span className="small muted">Lesson type</span>
        <select
          aria-label="Lesson type"
          value={selectedOfferingKey ?? ""}
          disabled={choicesLocked}
          onChange={(event) => onSelectOffering(event.target.value)}
        >
          {selectableOfferings.map((offering) => {
            const key = offeringKey(offering);
            const credits = offering.book_with_credit.allowed
              ? ` · ${offering.available_credits} credit${offering.available_credits === 1 ? "" : "s"}`
              : "";
            return (
              <option key={key} value={key}>
                {serviceName(offering)}{credits}
              </option>
            );
          })}
        </select>
      </label>

      {selectedOffering && fundingMode && (
        <div style={{ marginTop: 14 }}>
          <div className="stack">
            <span className="small muted">Funding</span>
            {modes.length > 1 ? (
              <div className="row">
                <label>
                  <input
                    type="radio"
                    name="tutor-booking-funding"
                    checked={fundingMode === "lesson_credit"}
                    disabled={busy !== null}
                    onChange={() => onFundingMode("lesson_credit")}
                  />{" "}
                  Use lesson credit
                </label>
                <label>
                  <input
                    type="radio"
                    name="tutor-booking-funding"
                    checked={fundingMode === "payment_pending"}
                    disabled={busy !== null}
                    onChange={() => onFundingMode("payment_pending")}
                  />{" "}
                  Payment pending
                </label>
              </div>
            ) : (
              <strong>{fundingMode === "lesson_credit" ? "Use lesson credit" : "Payment pending"}</strong>
            )}
          </div>

          {!appointmentMode && <div className="availability-controls booking-availability-controls">
            <div className="row segmented-control" role="group" aria-label="Availability range">
              <button
                aria-pressed={availabilityRange === "day"}
                className={availabilityRange === "day" ? "action-primary" : "action-tertiary"}
                disabled={busy !== null}
                onClick={() => onAvailabilityRange("day")}
                type="button"
              >
                Day
              </button>
              <button
                aria-pressed={availabilityRange === "week"}
                className={availabilityRange === "week" ? "action-primary" : "action-tertiary"}
                disabled={busy !== null}
                onClick={() => onAvailabilityRange("week")}
                type="button"
              >
                Week
              </button>
            </div>
            <div className="row">
              <button
                aria-label={`Previous ${availabilityRange}`}
                className="action-tertiary"
                disabled={busy !== null || preferredDate <= today}
                onClick={() => onMoveAvailabilityRange(-1)}
                type="button"
              >
                ← Previous
              </button>
              <strong className="availability-window">
                {availabilityRange === "day"
                  ? formatFullDay(window.startDate)
                  : `${formatFullDay(window.startDate)} – ${formatFullDay(
                      moveAvailabilityWindow(window.endDate, "day", -1),
                    )}`}
              </strong>
              <button
                aria-label={`Next ${availabilityRange}`}
                className="action-tertiary"
                disabled={busy !== null}
                onClick={() => onMoveAvailabilityRange(1)}
                type="button"
              >
                Next →
              </button>
            </div>
          </div>}

          <div className="row"
          data-booking-date-controls style={{ alignItems: "flex-end", marginTop: 10, flexWrap: "wrap" }}>
            <DateField
              label={appointmentMode ? "Date" : "Jump to date"}
              min={appointmentMode ? today : undefined}
              value={preferredDate}
              disabled={choicesLocked}
              onChange={onPreferredDate}
            />
            <button
              className="action-secondary"
              disabled={busy !== null || preferredDate === ""}
              onClick={onFindTimes}
              type="button"
            >
              {busy === "availability" ? "Looking for times…" : appointmentMode ? "Refresh times" : `Show this ${availabilityRange}`}
            </button>
          </div>

          {error && (uncertain ? <ActionConfirmation variant="uncertain" title="Booking outcome needs checking" description={error} reveal /> : <div className="error" style={{ marginTop: 10 }}>{error}</div>)}

          {availability !== null && availability.length === 0 && selectedStart === null && (
            <p className="empty">No normal available times in this {availabilityRange}. Try another {availabilityRange}.</p>
          )}

          {availability !== null && availability.length > 0 && (
            <div className="availability-days" style={{ marginTop: 12 }}>
              {groupAvailabilitySlots(availability).map(([date, daySlots]) => (
                <section className="availability-day" key={date}>
                  <h4 className="day-heading">{formatDay(daySlots[0]!.starts_at)}</h4>
                  <div className="slot-grid">
                    {daySlots.map((slot) => (
                      <button
                        key={slot.starts_at}
                        className={slot.starts_at === selectedStart ? "action-primary" : "action-tertiary"}
                        disabled={busy !== null}
                        onClick={() => onSelectStart(slot.starts_at)}
                        type="button"
                      >
                        {formatTime(slot.starts_at)}
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}

          {selectedStart !== null && (
            <div className="notice" style={{ marginTop: 12 }}>
              <p style={{ marginTop: 0 }}>
                <strong>
                  Book {serviceName(selectedOffering)}{studentName ? ` for ${studentName}` : ""} on {formatDate(selectedStart)} at{" "}
                  {formatTime(selectedStart)}.
                </strong>
              </p>
              {workingHoursOverride && (
                <div className="notice small" role="alert">
                  <strong>{tutorBookingScheduleOverrideMessage(workingHoursCheck)}</strong>
                </div>
              )}
              <p className="small">
                {fundingMode === "lesson_credit"
                  ? "1 lesson credit will be used."
                  : "Payment will remain due and can be marked as received later."}
              </p>
              <BookingConfirmationEmailControl
                checked={notifyParticipants}
                disabled={busy === "booking" || uncertain || notifyParticipantsDisabled}
                onChange={onNotifyParticipants}
              />
              <button disabled={busy !== null} onClick={onConfirm} type="button">
                {busy === "booking"
                  ? "Booking…"
                  : workingHoursOverride
                    ? "Book anyway"
                    : "Book lesson"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function TutorBookLesson({
  appointmentMode = false,
  showHeading = true,
  studentId,
  studentName = null,
  blocks,
  selectedTutorProviderId = null,
  selectedTutorName = null,
  onBooked,
  onMutationBusyChange,
  initialStartsAt = null,
}: {
  /** Diary add action: one upcoming lesson, published slots only, automatic day lookup. */
  appointmentMode?: boolean;
  /** Hosts that already name the lesson form can omit its repeated heading. */
  showHeading?: boolean;
  studentId: string;
  studentName?: string | null;
  blocks: LessonCreditBlock[];
  selectedTutorProviderId?: number | null;
  selectedTutorName?: string | null;
  onBooked: () => void;
  /** Lets a host drawer disable its dismiss affordances while a booking writes. */
  onMutationBusyChange?: (busy: boolean) => void;
  /**
   * A calendar-click hint, not proof that the slot is free. A missing published
   * slot is checked exactly: WMF-SIte may identify it as a safe working-hours
   * override, but a genuine conflict is still rejected.
   */
  initialStartsAt?: string | null;
}) {
  const publish = usePublishActionOutcome();
  const [hosted, setHosted] = useState(false);
  const [offerings, setOfferings] = useState<BookingOffering[] | null>(null);
  const [selectedOfferingKey, setSelectedOfferingKey] = useState<string | null>(null);
  const [fundingMode, setFundingMode] = useState<TutorFundingMode | null>(null);
  const [bookingMode, setBookingMode] = useState<TutorBookingMode>("one");
  const [notifyParticipants, setNotifyParticipants] = useState(false);
  const [preferredDate, setPreferredDate] = useState(() =>
    appointmentMode ? "" : initialStartsAt === null ? isoDay(0) : dayKey(initialStartsAt),
  );
  const [range, setRange] = useState<AvailabilityRange>(() =>
    appointmentMode || initialStartsAt !== null ? "day" : "week",
  );
  const [availability, setAvailability] = useState<StaffAvailability["slots"] | null>(null);
  const [selectedStart, setSelectedStart] = useState<string | null>(null);
  const [workingHoursCheck, setWorkingHoursCheck] = useState<StaffSlotCheck | null>(null);
  const [busy, setBusy] = useState<"offerings" | "availability" | "booking" | null>("offerings");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [key, setKey] = useState(idempotencyKey);
  // Retain the chosen calendar time across read retries and service changes;
  // every lookup validates it again rather than reusing an override decision.
  const calendarHint = useRef(appointmentMode ? null : initialStartsAt);
  const availabilityRequest = useRef(0);
  const window = availabilityWindow(preferredDate || isoDay(0), range);

  // Only a booking write is worth blocking a host drawer's dismiss for; the
  // offerings/availability reads are safe to abandon. Always hand back "not
  // busy" on the way out so unmounting mid-write cannot leave a drawer stuck.
  useEffect(() => {
    onMutationBusyChange?.(busy === "booking");
    return () => onMutationBusyChange?.(false);
  }, [busy, onMutationBusyChange]);

  useEffect(() => {
    let active = true;
    setBusy("offerings");
    setError(null);
    setOfferings(null);
    setSelectedOfferingKey(null);
    setFundingMode(null);
    setBookingMode("one");
    setNotifyParticipants(false);
    setWorkingHoursCheck(null);
    void tutorOneOffBooking.offerings(studentId, selectedTutorProviderId).then(
      (result) => {
        if (!active) return;
        const scoped = result.offerings.filter(
          (offering) => selectedTutorProviderId === null || offering.provider_id === selectedTutorProviderId,
        );
        const first = defaultBookingOffering(
          scoped.filter((offering) => defaultFundingMode(offering) !== null),
        );
        setOfferings(scoped);
        setSelectedOfferingKey(first === null ? null : offeringKey(first));
        setFundingMode(first === null ? null : defaultFundingMode(first));
        setBusy(null);
      },
      (cause: unknown) => {
        if (!active) return;
        setError(tutorOfferingFailure(cause));
        setBusy(null);
      },
    );
    return () => {
      active = false;
    };
  }, [studentId, selectedTutorProviderId]);

  const selectedOffering =
    offerings?.find((offering) => offeringKey(offering) === selectedOfferingKey) ?? null;

  function nameOffering(offering: BookingOffering): string {
    return (
      offering.service_name ??
      blocks.find(
        (block) => block.provider_id === offering.provider_id && block.service_id === offering.service_id,
      )?.service_name ??
      `Lesson service ${offering.service_id}`
    );
  }

  const recurrenceContext = useMemo<RecurringLessonContext | null>(() => {
    if (selectedOffering === null) return null;
    return {
      studentId,
      studentName,
      providerId: selectedOffering.provider_id,
      providerName: selectedTutorName,
      serviceId: selectedOffering.service_id,
      serviceName: nameOffering(selectedOffering),
      initialStartsAt,
    };
  }, [
    initialStartsAt,
    selectedOffering,
    selectedTutorName,
    studentId,
    studentName,
  ]);

  function resetAvailability() {
    availabilityRequest.current += 1;
    // The abandoned read can no longer clear its own busy state.
    setBusy((current) => (current === "availability" ? null : current));
    setAvailability(null);
    setSelectedStart(null);
    setWorkingHoursCheck(null);
    setError(null);
  }

  function chooseOffering(nextKey: string) {
    const next = offerings?.find((offering) => offeringKey(offering) === nextKey) ?? null;
    if (nextKey !== selectedOfferingKey) setKey(idempotencyKey());
    setSelectedOfferingKey(nextKey);
    setFundingMode(next === null ? null : defaultFundingMode(next));
    resetAvailability();
  }

  function chooseFunding(nextMode: TutorFundingMode) {
    if (nextMode !== fundingMode) setKey(idempotencyKey());
    setFundingMode(nextMode);
    setError(null);
  }

  function chooseDate(day: string) {
    calendarHint.current = null;
    setPreferredDate(day);
    resetAvailability();
    // Typing a year digit by digit briefly reports 0002-, 0020-, 0202-...;
    // those are unfinished, not a past date to warn about.
    const yearComplete = Number(day.slice(0, 4)) >= 1000;
    if (appointmentMode && day !== "" && yearComplete && day < isoDay(0)) {
      setError("Choose today or a future date.");
    }
    setKey(idempotencyKey());
  }

  function chooseRange(nextRange: AvailabilityRange) {
    if (nextRange === range) return;
    setRange(nextRange);
    resetAvailability();
    setKey(idempotencyKey());
  }

  function moveRange(direction: -1 | 1) {
    calendarHint.current = null;
    setPreferredDate((current) => moveAvailabilityWindow(current, range, direction));
    resetAvailability();
    setKey(idempotencyKey());
  }

  function chooseStart(startsAt: string) {
    calendarHint.current = null;
    if (startsAt !== selectedStart) setKey(idempotencyKey());
    setSelectedStart(startsAt);
    setWorkingHoursCheck(null);
    setError(null);
  }

  /** `notice` keeps a message (such as why a booking was refused) above the fresh times. */
  async function findTimes(notice: string | null = null) {
    if (!selectedOffering || preferredDate === "" || (appointmentMode && preferredDate < isoDay(0))) return;
    const request = ++availabilityRequest.current;
    setBusy("availability");
    setAvailability(null);
    setError(notice);
    setSelectedStart(null);
    setWorkingHoursCheck(null);
    try {
      const hinted = calendarHint.current;
      const availabilityRead = staffBooking.availability({
        provider_id: String(selectedOffering.provider_id),
        service_id: String(selectedOffering.service_id),
        start_date: window.startDate,
        end_date: window.endDate,
      });
      const exactRead = hinted === null
        ? Promise.resolve(null)
        : staffWorkingHours.check({
          providerId: selectedOffering.provider_id,
          serviceId: selectedOffering.service_id,
          startsAt: hinted,
        }).then((value) => ({ value }), (error: unknown) => ({ error }));
      const [result, exactResult] = await Promise.all([availabilityRead, exactRead]);
      if (request !== availabilityRequest.current) return;
      setAvailability(result.slots);

      if (hinted !== null) {
        if (result.slots.some((slot) => slot.starts_at === hinted)) {
          setSelectedStart(hinted);
        } else {
          if (exactResult === null || "error" in exactResult) {
            throw exactResult !== null && "error" in exactResult
              ? exactResult.error
              : new Error("The exact time could not be checked.");
          }
          const exact = exactResult.value;
          if (
            exact.state === "available" ||
            exact.schedule_override_available ||
            exact.working_hours_override_available
          ) {
            setSelectedStart(hinted);
            setWorkingHoursCheck(exact);
          } else {
            setError(
              "The time you clicked is not available and cannot be overridden. Nothing was booked; choose one of the current times below.",
            );
          }
        }
      }
    } catch (cause) {
      if (request === availabilityRequest.current) {
        const failure = tutorAvailabilityFailure(cause);
        setError(notice === null ? failure : `${notice} ${failure}`);
      }
    } finally {
      if (request === availabilityRequest.current) setBusy(null);
    }
  }

  useEffect(() => {
    if ((appointmentMode || calendarHint.current !== null) && preferredDate !== "" && selectedOfferingKey !== null) {
      void findTimes();
    }
    return () => { availabilityRequest.current += 1; };
    // A date or service change invalidates the previous availability snapshot.
    // Funding does not change the provider/service's available times.
  }, [appointmentMode, preferredDate, selectedOfferingKey]);

  async function confirmBooking() {
    if (!selectedOffering || selectedStart === null || fundingMode === null) return;
    const overrideWorkingHours = tutorBookingWorkingHoursOverride(workingHoursCheck, selectedStart);
    let refreshNotice: string | null = null;
    setBusy("booking");
    setError(null);
    try {
      const notifyParticipantsUsedForBooking = notifyParticipants;
      const input = {
        provider_id: selectedOffering.provider_id,
        service_id: selectedOffering.service_id,
        starts_at: selectedStart,
        notify_participants: notifyParticipantsUsedForBooking,
      };
      const result: StaffLessonBookingResult = await measureAction("lesson.book", () =>
        fundingMode === "lesson_credit"
          ? staffBooking.creditFundedBooking(studentId, input, key, overrideWorkingHours)
          : tutorOneOffBooking.paymentPendingBooking(
              studentId,
              input,
              key,
              overrideWorkingHours,
            ),
      );
      setDone(tutorBookingSuccess(result));
      const appointmentId = typeof result.appointment_id === "number" &&
        Number.isSafeInteger(result.appointment_id) && result.appointment_id > 0
        ? result.appointment_id
        : null;
      const bookedAt = Date.now();
      const undoAction = appointmentId === null ? undefined : (
        <UndoBookingButton
          appointmentId={appointmentId}
          bookedAt={bookedAt}
          notifyParticipants={notifyParticipantsUsedForBooking}
          studentName={studentName}
          startsAt={result.starts_at}
          onBooked={onBooked}
          onLocalOutcome={(next) => {
            setHosted(false);
            setOutcome(next);
          }}
        />
      );
      const receipt = tutorBookingOutcome(result, studentName, nameOffering(selectedOffering), undoAction);
      setOutcome(receipt);
      setHosted(publish(receipt));
      setUncertain(false);
      onBooked();
    } catch (cause) {
      const attempt = tutorBookingAttemptAfterFailure(key, cause);
      if (attempt.kind === "uncertain") setHosted(publish({ variant: "uncertain", title: "Booking outcome needs checking", description: studentName, details: [{ value: attempt.message }] }));
      setError(attempt.message);
      setUncertain(attempt.kind === "uncertain");
      setKey(attempt.key);
      if (attempt.kind === "rejected") {
        calendarHint.current = null;
        setAvailability(null);
        setSelectedStart(null);
        setWorkingHoursCheck(null);
        // The add action loads times by itself, so show the day's current
        // times again rather than asking for a manual refresh. Only a proven
        // rejection qualifies: an uncertain outcome keeps its attempt key.
        if (appointmentMode) refreshNotice = attempt.message;
      }
    } finally {
      setBusy(null);
    }
    if (refreshNotice !== null) await findTimes(refreshNotice);
  }

  const typeChoice = !appointmentMode && recurrenceContext !== null && done === null && (
    <div className="panel row tutor-booking-type" role="group" aria-label="Booking type">
      <span className="small muted">Book</span>
      <button
        type="button"
        className={bookingMode === "one" ? "cal-view is-on" : "cal-view"}
        aria-pressed={bookingMode === "one"}
        disabled={busy === "booking"}
        onClick={() => setBookingMode("one")}
      >
        One lesson
      </button>
      <button
        type="button"
        className={bookingMode === "repeats" ? "cal-view is-on" : "cal-view"}
        aria-pressed={bookingMode === "repeats"}
        disabled={busy === "booking"}
        onClick={() => setBookingMode("repeats")}
      >
        Repeats
      </button>
    </div>
  );

  if (done && hosted) return null;

  return (
    <>
      {typeChoice}
      {bookingMode === "repeats" && recurrenceContext !== null && done === null ? (
        <CreateRecurringLesson
          key={`${studentId}:${recurrenceContext.providerId}:${recurrenceContext.serviceId}:${initialStartsAt ?? ""}`}
          context={recurrenceContext}
          onCreated={onBooked}
        />
      ) : (
        <TutorBookLessonView
          appointmentMode={appointmentMode}
          showHeading={showHeading}
          offerings={offerings}
          selectedOfferingKey={selectedOfferingKey}
          onSelectOffering={chooseOffering}
          fundingMode={fundingMode}
          onFundingMode={chooseFunding}
          studentName={studentName}
          serviceName={nameOffering}
          preferredDate={preferredDate}
          onPreferredDate={chooseDate}
          availabilityRange={range}
          onAvailabilityRange={chooseRange}
          onMoveAvailabilityRange={moveRange}
          availability={availability}
          selectedStart={selectedStart}
          workingHoursCheck={workingHoursCheck}
          onSelectStart={chooseStart}
          onFindTimes={() => void findTimes()}
          onConfirm={() => void confirmBooking()}
          notifyParticipants={notifyParticipants}
          onNotifyParticipants={setNotifyParticipants}
          notifyParticipantsDisabled={uncertain}
          busy={busy}
          error={error}
          done={done}
          outcome={outcome}
          uncertain={uncertain && !hosted}
        />
      )}
    </>
  );
}
